/**
 * Promotion Engine - Auto Add/Remove Gift Handler
 * Safely interacts with LcCart AJAX API with locking prevention.
 */
class LcPromotionGift {
  static isMutating = false;

  /**
   * Sync active promotions with the cart by adding qualified gifts and removing unqualified ones.
   * @param {object} cart - Shopify Cart object
   * @param {Array<object>} activePromos - Sorted active promotions list
   */
  static async syncGifts(cart, activePromos) {
    if (this.isMutating) return;

    // Compile list of gifts that should be in the cart
    const qualifiedGifts = [];
    
    for (const promo of activePromos) {
      if (!promo.status || promo.status.toLowerCase() !== 'active' || !promo.giftVariantId) continue;
      
      // Check eligibility
      let isEligible = false;
      
      if (promo.type === 'buy_x_get_y' || promo.type === 'qty_percent' || promo.type === 'qty_fixed') {
        const qty = LcPromotionUtils.getQualifyingQuantity(cart, promo);
        isEligible = qty >= (promo.requiredQuantity || 0);
      } else if (promo.type === 'spend_gift' || promo.type === 'spend_shipping') {
        const total = LcPromotionUtils.getQualifyingSubtotal(cart, promo);
        isEligible = total >= (promo.minimumCartValue * 100 || 0); // Convert min value to cents
      }

      if (isEligible) {
        qualifiedGifts.push({
          id: parseInt(promo.giftVariantId),
          quantity: promo.giftQuantity || 1,
          promoId: promo.id,
          title: promo.title
        });
        
        // If priority system is strict and we only allow ONE highest-priority gift, break here:
        // (However, standard multi-gift promotions are supported by default)
      }
    }

    // Identify current gifts in cart (must be marked with _is_free_gift property)
    const cartGifts = cart.items.filter(item => {
      const isFreeGift = window.LcPromotionUtils ? window.LcPromotionUtils.isFreeGift(item) : false;
      return isFreeGift && activePromos.some(p => p.giftVariantId && parseInt(p.giftVariantId) === item.variant_id);
    });

    const updates = {};
    const toAdd = [];

    // 1. Check which qualified gifts need to be added or adjusted
    for (const qualified of qualifiedGifts) {
      const existing = cartGifts.find(item => item.variant_id === qualified.id);
      if (!existing) {
        toAdd.push({
          id: qualified.id,
          quantity: qualified.quantity,
          properties: {
            '_is_free_gift': 'true',
            '_associated_promo': qualified.promoId
          }
        });
      } else if (existing.quantity !== qualified.quantity) {
        // Correct quantity of gift if modified by customer
        updates[existing.key] = qualified.quantity;
      }
    }

    // 2. Identify which existing gifts in the cart are no longer qualified
    for (const existing of cartGifts) {
      const isStillQualified = qualifiedGifts.some(q => q.id === existing.variant_id);
      if (!isStillQualified) {
        updates[existing.key] = 0; // Remove unqualified gift
      }
    }

    // 3. Execute mutations
    try {
      if (toAdd.length > 0 || Object.keys(updates).length > 0) {
        this.isMutating = true;
        
        // Dispatch start event for animations
        document.dispatchEvent(new CustomEvent('lc:promotion:gift-mutation-start'));

        if (Object.keys(updates).length > 0) {
          await window.LcCart.update({ updates });
        }

        if (toAdd.length > 0) {
          await window.LcCart.add(toAdd);
          
          // Trigger Apple-level visual toast / animation for each added gift
          toAdd.forEach(item => {
            const promo = activePromos.find(p => parseInt(p.giftVariantId) === item.id);
            if (promo) {
              this.showGiftToast(promo);
            }
          });
        }
      }
    } catch (err) {
      console.error('Promotion Engine - Gift Sync Error:', err);
    } finally {
      this.isMutating = false;
      document.dispatchEvent(new CustomEvent('lc:promotion:gift-mutation-end'));
    }
  }

  /**
   * Displays a premium Apple-level slide-in notification toast when a gift is added.
   * @param {object} promo
   */
  static showGiftToast(promo) {
    const toast = document.createElement('div');
    toast.className = 'lc-promo-toast';
    toast.style.background = promo.bgColor || '#1a1a1a';
    toast.style.color = promo.textColor || '#ffffff';
    
    toast.innerHTML = `
      <div class="lc-promo-toast__inner">
        <span class="lc-promo-toast__icon">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
        </span>
        <div class="lc-promo-toast__content">
          <div class="lc-promo-toast__title">${promo.title} Unlocked!</div>
          <div class="lc-promo-toast__subtitle">A free gift has been added to your cart.</div>
        </div>
        <button class="lc-promo-toast__close" aria-label="Dismiss">&times;</button>
      </div>
    `;

    document.body.appendChild(toast);
    
    // Animate in
    setTimeout(() => toast.classList.add('is-active'), 10);

    // Auto dismiss
    const dismiss = () => {
      toast.classList.remove('is-active');
      setTimeout(() => toast.remove(), 400);
    };

    toast.querySelector('.lc-promo-toast__close').addEventListener('click', dismiss);
    setTimeout(dismiss, 5000);
  }
}

window.LcPromotionGift = LcPromotionGift;
